import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { Box, Button, Typography, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { devicesActions } from '../store';
import { useEffectAsync } from '../reactHelper';
import fetchOrThrow from '../common/util/fetchOrThrow';

import DeviceRowCompact from './DeviceRowCompact';

const PAGE_SIZE = 20;

const useStyles = makeStyles()((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxSizing: 'border-box',
    background: theme.palette.background.paper,
  },
  header: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1, 0.75, 1),
    background: theme.palette.background.paper,
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  count: {
    color: theme.palette.text.secondary,
  },
  divider: {
    flexShrink: 0,
  },
  scroller: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    padding: theme.spacing(0.75, 1, 1, 1),
    boxSizing: 'border-box',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  footer: {
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: theme.spacing(1),
  },
  empty: {
    height: '100%',
    minHeight: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    padding: theme.spacing(2),
  },
}));

const DeviceList = ({
  devices = [],
  positions = {},
  onSelect,
  bottomInset = 0,
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const selectedId = useSelector((state) => state.devices.selectedId);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const itemRefs = useRef({});

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffectAsync(async () => {
    const response = await fetchOrThrow('/api/devices');
    dispatch(devicesActions.refresh(await response.json()));
  }, [dispatch]);

  const safeDevices = useMemo(
    () => (Array.isArray(devices)
      ? devices.filter((device) => device && device.id != null)
      : []),
    [devices],
  );

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [safeDevices.length]);

  useEffect(() => {
    if (!selectedId || !safeDevices.length) return;

    const selectedIndex = safeDevices.findIndex((device) => device.id === selectedId);
    if (selectedIndex === -1) return;

    const requiredVisible = selectedIndex + 1;
    if (requiredVisible > visibleCount) {
      setVisibleCount((current) => Math.max(current, requiredVisible));
    }
  }, [selectedId, safeDevices, visibleCount]);

  const visibleDevices = useMemo(
    () => safeDevices.slice(0, Math.min(visibleCount, safeDevices.length)),
    [safeDevices, visibleCount],
  );

  const canLoadMore = visibleCount < safeDevices.length;

  useEffect(() => {
    const root = containerRef.current;
    const target = sentinelRef.current;

    if (!root || !target) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && canLoadMore) {
          setVisibleCount((current) => Math.min(current + PAGE_SIZE, safeDevices.length));
        }
      },
      {
        root,
        rootMargin: '900px',
        threshold: 0.01,
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [canLoadMore, safeDevices.length]);

  useEffect(() => {
    if (!selectedId) return;

    const el = itemRefs.current[selectedId];
    if (!el) return;

    el.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }, [selectedId]);

  const handleSelectInternal = useCallback((deviceId) => {
    if (onSelect) {
      onSelect(deviceId);
      return;
    }

    dispatch(devicesActions.selectId(deviceId));
  }, [dispatch, onSelect]);

  if (!safeDevices.length) {
    return (
      <Box className={classes.root}>
        <div className={classes.empty}>
          <Typography variant="body2">
            Nenhum veículo encontrado.
          </Typography>
        </div>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <div className={classes.header}>
        <Typography variant="subtitle2">
          Veículos
        </Typography>
        <Typography variant="caption" className={classes.count}>
          {safeDevices.length} item(ns)
        </Typography>
      </div>

      <Divider className={classes.divider} />

      <div
        className={classes.scroller}
        ref={containerRef}
        style={{
          paddingBottom: isMobile ? Math.max(bottomInset, 16) : 16,
        }}
      >
        <div className={classes.list}>
          {visibleDevices.map((device) => {
            const position = positions?.[device.id];

            return (
              <div
                key={device.id}
                ref={(node) => {
                  if (node) {
                    itemRefs.current[device.id] = node;
                  } else {
                    delete itemRefs.current[device.id];
                  }
                }}
              >
                <DeviceRowCompact
                  device={device}
                  position={position}
                  selected={device.id === selectedId}
                  onSelect={handleSelectInternal}
                />
              </div>
            );
          })}
        </div>

        <div ref={sentinelRef} style={{ height: 1 }} />

        {canLoadMore && (
          <div className={classes.footer}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setVisibleCount((current) => Math.min(current + PAGE_SIZE, safeDevices.length));
              }}
            >
              Carregar mais ({visibleCount}/{safeDevices.length})
            </Button>
          </div>
        )}
      </div>
    </Box>
  );
};

export default DeviceList;